import { Head, router } from "@inertiajs/react"
import { CirclePlus } from "lucide-react"
import { useState } from "react"

import { HpiDataTable, HpiFilterSelect } from "@/components/hpi"
import type { HpiColumn } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import { equipmentIndexPath, equipmentPath, newEquipmentPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface Equipment {
  id: number
  name: string
  display_name: string
  equipment_type: string | null
  status: number
  assigned_to: string | null
}

interface FilterOptions {
  equipment_types: { id: number; name: string }[]
  statuses: { value: string; label: string }[]
}

interface Filters {
  equipment_type_id: string
  status: string
}

interface Props {
  equipment: Equipment[]
  filter_options: FilterOptions
  filters: Filters
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Equipment", href: "/equipment" },
]

/* ── Filter Bar ────────────────────────────────────────── */

function FilterBar({
  filters,
  filterOptions,
}: {
  filters: Filters
  filterOptions: FilterOptions
}) {
  const [equipmentTypeId, setEquipmentTypeId] = useState(
    filters.equipment_type_id,
  )
  const [status, setStatus] = useState(filters.status)

  const handleUpdate = () => {
    const params: Record<string, string> = {}
    if (equipmentTypeId) params.equipment_type_id = equipmentTypeId
    if (status && status !== "active") params.status = status
    router.get(equipmentIndexPath(), params, { preserveState: true })
  }

  const handleClear = () => {
    setEquipmentTypeId("")
    setStatus("active")
    router.get(equipmentIndexPath(), {}, { preserveState: true })
  }

  const hasFilters = equipmentTypeId || status !== "active"

  return (
    <div className="bg-hpi-pale-gray mb-6 flex flex-wrap items-end gap-4 rounded-lg px-4 py-3">
      <HpiFilterSelect
        label="Type"
        value={equipmentTypeId}
        onChange={setEquipmentTypeId}
        options={filterOptions.equipment_types.map((t) => ({
          value: t.id,
          label: t.name,
        }))}
        placeholder="All Types"
      />
      <HpiFilterSelect
        label="Status"
        value={status}
        onChange={setStatus}
        options={filterOptions.statuses.map((s) => ({
          value: s.value,
          label: s.label,
        }))}
        placeholder="All"
      />
      <button
        onClick={handleUpdate}
        className="border-hpi-dark-blue text-hpi-dark-blue hover:bg-hpi-dark-blue h-8 rounded border px-5 text-sm font-medium transition-colors hover:text-white"
      >
        Update
      </button>
      {hasFilters && (
        <button
          onClick={handleClear}
          className="text-hpi-med-gray hover:text-hpi-dark-gray h-8 px-3 text-sm transition-colors"
        >
          Clear
        </button>
      )}
    </div>
  )
}

/* ── Assignment Status Badge ──────────────────────────── */

function AssignmentBadge({ assignedTo }: { assignedTo: string | null }) {
  if (assignedTo) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className="bg-hpi-dark-green inline-block h-1.5 w-1.5 rounded-full" />
        <span>{assignedTo}</span>
      </span>
    )
  }

  return (
    <span className="text-hpi-med-gray inline-flex items-center gap-1.5 text-xs">
      <span className="bg-hpi-med-gray inline-block h-1.5 w-1.5 rounded-full" />
      Available
    </span>
  )
}

/* ── Main Component ────────────────────────────────────── */

export default function Index({ equipment, filter_options, filters }: Props) {
  const isInactive = filters.status === "inactive"
  const isAll = filters.status === "all"
  const statusLabel = isInactive ? "Inactive" : isAll ? "All" : ""
  const tableTitle = [statusLabel, "Equipment"].filter(Boolean).join(" ")

  const columns: HpiColumn<Equipment>[] = [
    {
      header: "Type",
      width: "w-[160px]",
      render: (item) => (
        <span className={item.status === 1 ? "text-hpi-med-gray" : ""}>
          {item.equipment_type}
        </span>
      ),
      hideOnMobile: true,
    },
    {
      header: "Name",
      render: (item) => (
        <span
          className={`font-medium ${item.status === 1 ? "text-hpi-med-gray" : "text-hpi-dark-gray"}`}
        >
          {item.display_name}
          {item.status === 1 && (
            <span className="text-hpi-med-gray ml-1.5 text-xs font-normal italic">
              (inactive)
            </span>
          )}
        </span>
      ),
    },
    {
      header: "Assigned To",
      width: "w-[200px]",
      render: (item) =>
        item.status === 1 ? (
          <span className="text-hpi-med-gray text-xs">{item.assigned_to}</span>
        ) : (
          <AssignmentBadge assignedTo={item.assigned_to} />
        ),
    },
  ]

  const handleToggleStatus = () => {
    const params: Record<string, string> = {}
    if (filters.equipment_type_id)
      params.equipment_type_id = filters.equipment_type_id
    if (isInactive) {
      // switch back to active (default)
    } else {
      params.status = "inactive"
    }
    router.get(equipmentIndexPath(), params, { preserveState: false })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={tableTitle} />

      <div className="p-6">
        <FilterBar filters={filters} filterOptions={filter_options} />

        <HpiDataTable
          title={tableTitle}
          actions={[
            {
              label: "Add Equipment",
              icon: CirclePlus,
              href: newEquipmentPath(),
            },
          ]}
          columns={columns}
          data={equipment}
          keyFn={(item) => item.id}
          rowHref={(item) => equipmentPath(item.id)}
          emptyMessage="No equipment found."
        />

        {!isAll && (
          <div className="mt-4 text-center">
            <button
              onClick={handleToggleStatus}
              className="text-hpi-active-blue hover:text-hpi-dark-blue text-sm font-medium transition-colors"
            >
              {isInactive ? "View Active" : "View Inactive"}
            </button>
          </div>
        )}
      </div>
    </AppLayout>
  )
}
